/**
 * `/auth/return` — adopts the session saas-auth forwards after centralized login
 * (genesis handoff §3.3). Handles BOTH delivery modes plus the OAuth error arrival, and
 * scrubs the URL before doing anything else so tokens/codes never linger in history.
 *
 * The legacy mode's URL `refreshToken` is deliberately never read or stored: the HttpOnly
 * `journix_refresh*` cookie is the authoritative refresh credential.
 */
import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { authApi } from '@/api/auth';
import { errorCodeOf } from '@/api/errors';
import { AuthPending } from '@/auth/AuthPending';
import { useAuth } from '@/auth/AuthContext';
import { getAccessToken, getExpiresAt } from '@/auth/authStore';
import { parseAuthReturn } from '@/auth/returnTokens';
import { adoptTokenResponse, bootRefresh, popDeepLink } from '@/auth/silentRefresh';
import { useCopy } from '@/hooks/useCopy';

function scrubUrl(): void {
  window.history.replaceState(null, '', window.location.pathname);
}

function errorTarget(error: string, code?: string | null): string {
  const params = new URLSearchParams({ error });
  if (code) params.set('errorCode', code);
  return `/auth/error?${params.toString()}`;
}

export function AuthReturn() {
  const [target, setTarget] = useState<string | null>(null);
  const { setAuthed, bootstrapPrincipal } = useAuth();
  const ran = useRef(false);
  const { t } = useCopy();

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const arrival = parseAuthReturn(window.location.search, window.location.hash);
    // Scrub FIRST — before any await — so a slow exchange never leaves credentials in the
    // address bar or in session history.
    scrubUrl();

    const adoptStoredSession = async (): Promise<void> => {
      const token = getAccessToken();
      if (token) setAuthed(token, getExpiresAt());
      await bootstrapPrincipal();
      setTarget(popDeepLink() ?? '/');
    };

    void (async () => {
      if (arrival.mode === 'error') {
        setTarget(errorTarget(arrival.error, arrival.errorCode));
        return;
      }

      if (arrival.mode === 'legacy') {
        const expiresAt = arrival.expiresIn ? Date.now() + arrival.expiresIn * 1000 : null;
        setAuthed(arrival.accessToken, expiresAt);
        await bootstrapPrincipal();
        setTarget(popDeepLink() ?? '/');
        return;
      }

      if (arrival.mode === 'code') {
        try {
          if (!adoptTokenResponse(await authApi.exchangeCode(arrival.code))) {
            setTarget(errorTarget('exchange_failed'));
            return;
          }
        } catch (err) {
          setTarget(errorTarget('exchange_failed', errorCodeOf(err)));
          return;
        }
        await adoptStoredSession();
        return;
      }

      // No credentials forwarded: either we already hold a session, or the cookie-based
      // silent refresh can still mint one. Neither ⇒ the terminal error page (no auto-loop).
      if (getAccessToken()) {
        setTarget(popDeepLink() ?? '/');
        return;
      }
      if (await bootRefresh()) {
        await adoptStoredSession();
        return;
      }
      setTarget('/auth/error');
    })();
  }, [setAuthed, bootstrapPrincipal]);

  if (target) return <Navigate to={target} replace />;
  return <AuthPending message={t('auth.returning', 'Completing sign-in…')} />;
}
